import { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { DeveloperTrack } from "../types";
import { useLeadTrackStatuses } from "./use-lead-track-statuses.hook";
import { useUpdateTrackStatusMutation } from "./use-update-track-status-mutation.hook";
import { getFormFields } from "@/modules/lead-page/api/lead-page.v2.api";
import { transformFormData } from "@/modules/lead-page/utils";

// Default status colors based on common status types
const STATUS_COLORS: Record<string, string> = {
  pending: "#FFF3E0",
  review: "#E3F2FD",
  processing: "#F3E5F5",
  approved: "#E8F5E9",
  rejected: "#FFEBEE",
  hold: "#FFF8E1",
  completed: "#E0F2F1",
  default: "#F5F5F5",
};

// Helper to get a color for a status based on its name
const getStatusColor = (statusName: string): string => {
  const lowerName = statusName.toLowerCase();
  for (const [key, color] of Object.entries(STATUS_COLORS)) {
    if (lowerName.includes(key)) {
      return color;
    }
  }
  return STATUS_COLORS.default;
};

interface UseUpdateTrackStatusProps {
  track: DeveloperTrack | null;
  onSuccess?: () => void;
}

interface UseUpdateTrackStatusReturn {
  // Modal state
  isOpen: boolean;
  openModal: (track: DeveloperTrack) => void;
  closeModal: () => void;
  
  // Current track
  currentTrack: DeveloperTrack | null;
  
  // Selected status
  selectedStatusUId: string | null;
  selectedStatusName: string;
  setSelectedStatus: (uid: string | null, name: string) => void;
  
  // Comment
  comment: string;
  setComment: (comment: string) => void;
  
  // Statuses data
  statuses: { uid: string; name: string; color: string; form?: { uid: string } | null }[];
  statusesLoading: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  
  // Submit
  handleSubmit: (formData?: Record<string, unknown>) => Promise<void>;
  isSubmitting: boolean;
  isValid: boolean;
  
  // Has changes
  hasChanges: boolean;
  
  // Dynamic form
  formFields: any;
  isLoadingForm: boolean;
}

const useUpdateTrackStatus = (
  props: UseUpdateTrackStatusProps = { track: null }
): UseUpdateTrackStatusReturn => {
  const { track, onSuccess } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<DeveloperTrack | null>(track);
  const [selectedStatusUId, setSelectedStatusUId] = useState<string | null>(null);
  const [selectedStatusName, setSelectedStatusName] = useState("");
  const [comment, setComment] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  // Initialize state from track prop
  useEffect(() => {
    if (track) {
      setCurrentTrack(track);
      setSelectedStatusUId(track.statusUId || null);
      setSelectedStatusName(track.currentStatus || "");
    }
  }, [track]);

  // Fetch statuses from API
  const {
    data: statusesData,
    isLoading: statusesLoading,
  } = useLeadTrackStatuses({
    leadTrackUId: currentTrack?.trackUId || "",
    search: searchTerm,
    enabled: isOpen && !!currentTrack?.trackUId,
  });

  // Update status mutation
  const updateStatusMutation = useUpdateTrackStatusMutation({
    trackUId: currentTrack?.trackUId || "",
    onSuccess: () => {
      closeModal();
      onSuccess?.();
    },
  });

  // Transform statuses to expected format with colors
  const statuses = useMemo(() => {
    if (!statusesData?.results) return [];
    return statusesData.results.map((status) => ({
      uid: status.uid,
      name: status.name,
      color: status.color || getStatusColor(status.name),
      priority: status.priority,
      form: status.form,
      mappedLeadStatusId: status.mappedLeadStatusId,
    }));
  }, [statusesData]);

  // Find selected status object to get formId
  const selectedStatus = useMemo(() => 
    statuses.find(s => s.uid === selectedStatusUId),
    [statuses, selectedStatusUId]
  );

  // Fetch form fields if status has form
  const { data: formFields, isLoading: isLoadingFormQuery } = useQuery({
    queryKey: ["getFormFields", selectedStatus?.form?.uid],
    queryFn: () => getFormFields(String(selectedStatus?.form?.uid)),
    enabled: !!selectedStatus?.form?.uid,
    staleTime: 30000,
  });

  // Only show loading if we're actually fetching a form (when enabled)
  const isLoadingForm = !!selectedStatus?.form?.uid && isLoadingFormQuery;

  const openModal = useCallback((track: DeveloperTrack) => {
    setCurrentTrack(track);
    // Set current status as selected
    setSelectedStatusUId(track.statusUId || null);
    setSelectedStatusName(track.currentStatus || "");
    setComment("");
    setIsOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setTimeout(() => {
      setCurrentTrack(null);
      setSelectedStatusUId(null);
      setSelectedStatusName("");
      setComment("");
      setSearchTerm("");
    }, 300);
  }, []);

  const setSelectedStatus = useCallback((uid: string | null, name: string) => {
    setSelectedStatusUId(uid);
    setSelectedStatusName(name);
  }, []);

  // Check if selection has changed
  const hasChanges = useMemo(() => {
    if (!currentTrack) return false;
    return selectedStatusUId !== currentTrack.statusUId;
  }, [currentTrack, selectedStatusUId]);

  // Form is valid as soon as a status is picked — re-selecting the SAME status is allowed on
  // purpose. The backend records a fresh track status update either way, and that update is what
  // re-arms the automation rules on the lead, so an agent has to be able to restate where a track
  // stands. `hasChanges` is still exposed for callers that want to know whether it actually moved.
  // If there's a dynamic form, validation is handled by the form itself (via handleSubmit callback)
  const isValid = useMemo(() => {
    return selectedStatusUId !== null;
  }, [selectedStatusUId]);

  const handleSubmit = useCallback(async (formData?: Record<string, unknown>) => {
    if (!currentTrack || !selectedStatusUId) return;

    // If it's not a form submission, a status still has to be picked (it may be the current one)
    if (!formData && !isValid) return;

    // Extract comment from formData if present, otherwise use state comment
    let submissionComment = comment;
    if (formData && 'comment' in formData) {
        submissionComment = String(formData.comment);
    }

    // Transform form data if present
    let transformedFormData;
    if (formData && formFields) {
      transformedFormData = transformFormData(formData, formFields);
    } else if (formData) {
      // Fallback if formFields not available but formData is (unlikely with FormFactory)
      transformedFormData = formData;
    }

    await updateStatusMutation.mutateAsync({
      statusUId: selectedStatusUId,
      comment: submissionComment || undefined,
      formData: transformedFormData,
    });
  }, [isValid, currentTrack, selectedStatusUId, comment, updateStatusMutation, formFields]);

  return {
    isOpen,
    openModal,
    closeModal,
    currentTrack,
    selectedStatusUId,
    selectedStatusName,
    setSelectedStatus,
    comment,
    setComment,
    statuses,
    statusesLoading,
    searchTerm,
    setSearchTerm,
    handleSubmit,
    isSubmitting: updateStatusMutation.isLoading,
    isValid,
    hasChanges,
    formFields,
    isLoadingForm,
  };
};

export default useUpdateTrackStatus;
